import { useCallback, useEffect, useRef } from 'react'
import { V2L3AppHeader, V2L3DashboardAppHeaderContent } from '@fusion-advantage/formrender'
import { UserIdentity } from '@fusion-advantage/backcompat'
import { cid24, proposeForceDevRenderer, proposeSetSyncStrategy, proposeShowDebugInfo, proposeSimulateOffline, SyncResultStatus, SyncStrategy } from '@fusion-advantage/formlib'
import { useAppDispatch, useAppSelector, useAppStore } from '../../../redux/hooks'
import { updateRenderState } from '../../../lib/relay/updateRenderState'
import { AppModal, pushAppModal, setForceDevRenderer, setSimulateOffline, setSyncStrategy } from '../../../redux/appSlice'
import { selectSyncResultStatus } from '../../../redux/syncSlice'
import { selectLocationStatus } from '../../../redux/locationSlice'

interface LxDashboardAppHeaderProps {
	displayIdentity: UserIdentity
}

export const LxDashboardAppHeader = ({displayIdentity}: LxDashboardAppHeaderProps) => {
	const instanceId = useRef<string>(cid24('i'))
	const appStore = useAppStore()
	const dispatch = useAppDispatch()
	useEffect(() => {
		dispatch(updateRenderState({t1NavAvailableFor: {[instanceId.current]: true}}))
		return () => { dispatch(updateRenderState({t1NavAvailableFor: {[instanceId.current]: false}})) }
	}, [appStore, dispatch])
	const authedIdentity = useAppSelector(state => state.dashboardData.authedIdentity)
	const env = useAppSelector(state => state.app.environment)
	const showDebugInfo = useAppSelector(state => state.render.showDebugInfo)
	const forceDevRenderer = useAppSelector(state => state.app.forceDevRenderer)
	const simulateOffline = useAppSelector(state => state.app.simulateOffline)
	const syncStrategy = useAppSelector(state => state.app.syncStrategy)
	const locationStatus = useAppSelector(selectLocationStatus)
	const syncStatus = useAppSelector(selectSyncResultStatus)
	const handleSimulateOfflineClick = useCallback(() => {
		if(syncStatus === SyncResultStatus.Pending) {
			alert('There is a sync currently in progress; please allow it to complete before going offline.')
			return
		}
		dispatch(setSimulateOffline(!simulateOffline))
	}, [dispatch, syncStatus, simulateOffline])
	return (
		<V2L3AppHeader
			hasPhantomShmoopie={true}
			row1Children={
				<V2L3DashboardAppHeaderContent
					userFullName={displayIdentity.fullName}
					canShowDebugInfo={proposeShowDebugInfo(authedIdentity, env).isOk()}
					isShowDebugInfo={showDebugInfo}
					onShowDebugInfoClick={() => dispatch(updateRenderState({showDebugInfo: !showDebugInfo}))}
					canForceDevRenderer={proposeForceDevRenderer(authedIdentity, env).isOk()}
					isForceDevRenderer={forceDevRenderer}
					onForceDevRendererClick={() => dispatch(setForceDevRenderer(!forceDevRenderer))}
					canSimulateOffline={proposeSimulateOffline(authedIdentity, env).isOk()}
					isSimulateOffline={simulateOffline}
					onSimulateOfflineClick={handleSimulateOfflineClick}
					canSetSyncStrategy={proposeSetSyncStrategy(authedIdentity, env).isOk()}
					isMobileSyncStrategy={syncStrategy === SyncStrategy.Mobile}
					onSyncStrategyClick={() => dispatch(setSyncStrategy(syncStrategy === SyncStrategy.Mobile ? SyncStrategy.Online : SyncStrategy.Mobile))}
					locationStatus={locationStatus}
					onLocationClick={() => dispatch(pushAppModal(AppModal.Location))}
					syncStatus={syncStatus}
					onSyncClick={() => dispatch(pushAppModal(AppModal.Sync))}
					onFeedbackClick={() => dispatch(pushAppModal(AppModal.Feedback))}
				/>
			}
		>
		</V2L3AppHeader>
	)
}
